import React, { useEffect, useState } from "react";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Trash2, Play, ImageIcon } from "lucide-react";

export default function Gallery() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("token");

  const load = async () => setItems((await api.get("/media")).data);
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("workspace_id", localStorage.getItem("workspace_id"));
    fd.append("caption", caption);
    fd.append("file", file);
    try {
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to gallery");
      setCaption("");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async (id) => { await api.delete(`/media/${id}`); setItems(items.filter((i) => i.id !== id)); };
  const fileUrl = (i) => `${API}/media/${i.id}/file?auth=${token}`;

  return (
    <div>
      <PageHeader eyebrow="Media" title="Photo & Video Gallery"
        subtitle="Upload photos and videos to showcase your Umrah tours"
        action={
          <label>
            <input type="file" className="hidden" onChange={upload} data-testid="media-upload-input" accept="image/*,video/*" />
            <span data-testid="media-upload-trigger" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold text-sm cursor-pointer">
              <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload Photo / Video"}
            </span>
          </label>
        }
      />
      <div className="p-4 sm:p-8">
        <Input data-testid="media-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)}
          placeholder="Optional caption for the next upload (e.g. Makkah 2026 group)" className="max-w-md mb-5 bg-card/50" />

        {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-12">No media yet. Upload your first photo or video.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((i) => (
            <Card key={i.id} data-testid={`media-card-${i.id}`} className="overflow-hidden bg-card/40 border-border/60 card-hover group relative">
              <div className="aspect-video bg-black/40 flex items-center justify-center overflow-hidden">
                {i.kind === "video" ? (
                  <video src={fileUrl(i)} controls className="w-full h-full object-cover" />
                ) : (
                  <img src={fileUrl(i)} alt={i.caption || i.original_filename} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm truncate flex items-center gap-1.5">
                    {i.kind === "video" ? <Play className="w-3 h-3 text-gold" /> : <ImageIcon className="w-3 h-3 text-gold" />}
                    {i.caption || i.original_filename}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{i.uploaded_by}</p>
                </div>
                {isOwner && (
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive flex-shrink-0" data-testid={`media-delete-${i.id}`} onClick={() => remove(i.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
